import React from 'react';
import styled from 'styled-components';
import { Message } from '../types';

interface MessageBubbleProps {
  message: Message;
}

const MessageContainer = styled.div<{ type: 'user' | 'ai' | 'system' }>`
  margin: 0.75rem 0;
  display: flex;
  align-items: flex-start;
  ${({ type }) => type === 'user' && 'justify-content: flex-end;'}
`;

const MessageBubble = styled.div<{ type: 'user' | 'ai' | 'system' }>`
  max-width: 80%;
  padding: 1rem 1.25rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all ${({ theme }) => theme.transitions.base};
  position: relative;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  ${({ type, theme }) => {
    switch (type) {
      case 'user':
        return `
          background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryHover});
          color: white;
          border-bottom-right-radius: ${theme.borderRadius.sm};
          
          &::after {
            content: '';
            position: absolute;
            bottom: 0;
            right: -8px;
            width: 0;
            height: 0;
            border-left: 8px solid ${theme.colors.primary};
            border-bottom: 8px solid transparent;
          }
        `;
      case 'ai':
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.textPrimary};
          border: 1px solid ${theme.colors.borderLight};
          border-bottom-left-radius: ${theme.borderRadius.sm};
          
          &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: -8px;
            width: 0;
            height: 0;
            border-right: 8px solid ${theme.colors.surface};
            border-bottom: 8px solid transparent;
          }
        `;
      case 'system':
        return `
          background: ${theme.colors.surfaceSecondary};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.colors.borderLight};
          font-style: italic;
          text-align: center;
          margin: 0 auto;
        `;
    }
  }}
`;

const MessageContent = styled.div`
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.6;
`;

const MessageTime = styled.div<{ type: 'user' | 'ai' | 'system' }>`
  font-size: 0.75rem;
  margin-top: 0.5rem;
  opacity: 0.7;
  ${({ type }) => type === 'user' ? 'text-align: right;' : 'text-align: left;'}
`;

const MessageBubbleComponent: React.FC<MessageBubbleProps> = ({ message }) => {
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('ja-JP', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <MessageContainer type={message.type}>
      <MessageBubble type={message.type}>
        <MessageContent>{message.content}</MessageContent>
        <MessageTime type={message.type}>
          {formatTime(message.timestamp)}
        </MessageTime>
      </MessageBubble>
    </MessageContainer>
  );
};

export default MessageBubbleComponent;